import {
  Avatar,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { amber, green, grey, red } from '@material-ui/core/colors';
import {
  Alert,
  Autocomplete,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@material-ui/lab';
import { SnackbarContext } from 'components';
import { Config, DateTimeFormat } from 'config';
import { format, parseISO } from 'date-fns';
import { usePrevious } from 'hooks';
import {
  addComment,
  addNps,
  fetchNpsComments,
  fetchNpsCompanies,
  fetchNpsDetails,
  fetchNpsHistory,
  fetchNpsLeaders,
  fetchNpsTalexioUsers,
  fetchTags,
  updateNps,
} from 'lib/Api/Nps';
import { getUserData } from 'lib/Helper/Auth';
import {
  NpsComment,
  NpsCompany,
  NpsHistoryItem,
  NpsLeader,
  NpsResponse,
  NpsTag,
  NpsTalexioUser,
} from 'lib/Model/Nps';
import * as React from 'react';
import { resolvePlatformNameFromPrefix } from 'views/Clients/components/SuggestedMatches';
import { NpsCommentBox, NpsCommentBoxHandle } from './NpsCommentBox';
import { RichTextEditor } from './components/RichTextEditor';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  ChevronLeft,
  ChevronRight,
  FormatQuote,
  Send,
} from '@material-ui/icons';
import { fetchJobDetails } from 'lib/Api/Jobs';
import { Job } from 'lib/Model/Job';
import JobDetailsDialog from 'views/Clients/components/JobDetailsDialog';

const useStyles = makeStyles((theme) => ({
  radio: {
    padding: theme.spacing(0.25),
  },
  capitlizeText: {
    textTransform: 'capitalize',
  },
  label: {
    fontSize: '0.875rem',
  },
  disabledInput: {
    '& .Mui-disabled': {
      color: 'black',
    },
  },
  formControl: {
    marginBottom: theme.spacing(1),
  },
  ratingGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(11, minmax(0, 1fr))',
  },
  ratingButton: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    height: '30px',
    fontSize: '0.75rem',
  },
  noSubmissionButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(0.5, 1),
    height: '30px',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  customFormControlLabel: {
    margin: '0 !important',
    padding: `${theme.spacing(0.25)}px !important`,
    '& .MuiRadio-root': {
      padding: theme.spacing(0.25),
    },
  },
  section: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    '& .MuiFormLabel-root': {
      // marginTop: theme.spacing(1), // Removed to allow more control with Grid
    },
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1), // Added margin for standalone titles
    // flexGrow: 1, // Removed as it's not always in a flex container
    // marginRight: theme.spacing(2), // Removed
  },
  sectionTitleContainer: {
    // Kept for Tags section
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
    '& $sectionTitle': {
      // Target sectionTitle specifically within this container
      flexGrow: 1,
      marginRight: theme.spacing(2),
      marginBottom: 0, // Reset marginBottom for this specific case
      borderBottom: 'none', // Reset from original style
      paddingBottom: 0, // Reset from original style
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    maxHeight: '85vh',
  },
  rightColumn: {
    height: '100%',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControlLabel-label': {
      whiteSpace: 'nowrap',
    },
  },
  commentSection: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    '& > form': {
      marginTop: 'auto',
    },
    '& > div': {
      overflowY: 'auto',
      flexGrow: 1,
    },
  },
  denseInput: {
    '& .MuiInputBase-input': {
      padding: theme.spacing(1),
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  gridContainer: {
    margin: 0,
    width: '100%',
  },
  gridItem: {
    padding: theme.spacing(0.5),
  },
  headerSection: {
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
      margin: 0,
    },
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
    },
  },
  compactText: {
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  noSubmissionRadio: {
    gridColumn: '1 / -1',
    justifyContent: 'flex-start !important',
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
  },
  checkboxGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    '& .MuiFormControlLabel-root': {
      margin: 0,
      marginRight: theme.spacing(2),
    },
    '& .MuiFormControlLabel-label': {
      whiteSpace: 'nowrap',
    },
  },
  compactTextfield: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        top: 0,
      },
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  tagChip: {
    marginLeft: theme.spacing(1),
  },
  testimonialChip: {
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '0.9rem',
      marginLeft: theme.spacing(0.75),
    },
  },
  filterGroup: {
    whiteSpace: 'nowrap',
  },
  toggleButton: {
    padding: theme.spacing(0.5, 1),
  },
  vacancyButton: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: '4px',
    textDecoration: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    padding: theme.spacing(0.5, 1), // Added padding for better appearance
    marginRight: theme.spacing(1), // Added margin for spacing if multiple links
    marginBottom: theme.spacing(0.5), // Added margin for spacing if multiple links
  },
  historyChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0),
  },
  historyChip: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: `1px solid ${grey[300]}`,
    backgroundColor: grey[100],
    color: grey[800],
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  historyChipPromoter: {
    borderColor: green[200],
    backgroundColor: green[50],
    color: green[800],
  },
  historyChipDetractor: {
    borderColor: red[200],
    backgroundColor: red[50],
    color: red[700],
  },
  historyChipCurrent: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
  },
  historyFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
  },
  linksContainer: {
    // New style for links container
    marginTop: theme.spacing(1),
  },
}));

interface Props {
  selectedNpsResponseId?: string;
  open: boolean;
  defaultCompanyId?: string;
  onClose(): void;
}

type TagParity = 'positive' | 'negative' | 'other' | 'all';

// Primary brand colours from the brand cheat sheet (JIM uses its secondary)
export const brandList = [
  {
    label: 'Konnekt',
    brand_identifier: 'knt',
    color: '#65B9E8',
  },
  {
    label: 'Konnekt Payroll',
    brand_identifier: 'pm',
    color: '#16466e',
  },
  {
    label: 'jobsinmalta',
    brand_identifier: 'jim',
    color: '#FC1EFF',
  },
  {
    label: 'Salariesinmalta',
    brand_identifier: 'sim',
    color: '#41B9BC',
  },
  {
    label: 'Talexio',
    brand_identifier: 'tlx',
    color: '#FD6A68',
  },
];

const userTypes = [
  {
    label: 'Candidate',
    key: 'candidate',
    brand_identifier: 'knt',
  },
  {
    label: 'TCN Candidate',
    key: 'candidate-tcn',
    brand_identifier: 'knt',
  },
  {
    label: 'Executive Candidate',
    key: 'candidate-executive',
    brand_identifier: 'knt',
  },
  {
    label: 'Client',
    key: 'client',
    brand_identifier: 'knt',
  },
  {
    label: 'Admin',
    key: 'admin',
    brand_identifier: 'tlx',
  },
  {
    label: 'Non-Admin',
    key: 'non-admin',
    brand_identifier: 'tlx',
  },
  {
    label: 'SIM Client',
    key: 'client',
    brand_identifier: 'sim',
  },
  {
    label: 'PM Client',
    key: 'client',
    brand_identifier: 'pm',
  },
  {
    label: 'JIM Client',
    key: 'client',
    brand_identifier: 'jim',
  },
];

export const NpsDialog: React.FC<Props> = ({
  selectedNpsResponseId,
  defaultCompanyId,
  open,
  onClose,
}) => {
  const snackbar = React.useContext(SnackbarContext);
  // The snackbar context value is re-created every render, so keep a stable
  // ref for use inside memoized callbacks (avoids re-creating fetchers and
  // re-triggering their effects on every render).
  const snackbarRef = React.useRef(snackbar);
  snackbarRef.current = snackbar;
  const classes = useStyles();
  const [comment, setComment] = React.useState<string | undefined>();

  const [loadingNps, setLoadingNps] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);
  const [loadingTags, setLoadingTags] = React.useState(false);

  const [loadingCompanies, setLoadingCompanies] = React.useState(false);
  const [loadingNpsLeaders, setLoadingNpsLeaders] = React.useState(false);
  const [loadingTalexioUsers, setLoadingTalexioUsers] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);

  const [comments, setComments] = React.useState<NpsComment[]>([]);
  const [history, setHistory] = React.useState<NpsHistoryItem[]>([]);
  // Rating history can open a different submission than the one passed in
  const loadedNpsIdRef = React.useRef<string>();
  const [tags, setTags] = React.useState<NpsTag[]>([]);
  const [companies, setCompanies] = React.useState<NpsCompany[]>([]);
  const [npsLeaders, setNpsLeaders] = React.useState<NpsLeader[]>([]);
  const [talexioUsers, setTalexioUsers] = React.useState<NpsTalexioUser[]>([]);
  const [dateHandled, setdateHandled] = React.useState<Date | null>(null);
  const [isTtvSubmission, setTtvSubmission] = React.useState<boolean>(false);
  const [selectedTags, setSelectedTags] = React.useState<NpsTag[]>([]);
  const [tagParityFilter, setTagParityFilter] =
    React.useState<TagParity>('all');

  const getDefaultNpsResponse = React.useCallback((): NpsResponse => {
    return {
      platform_nps_id: '',
      brand_identifier: brandList[0].brand_identifier,
      user_type: userTypes[0].key,
      company: '',
      company_id: defaultCompanyId,
      date_sent: '',
      user_first_name: '',
      user_last_name: '',
      user_email: '',
      user_contact_number: '',
      rating: -1,
      tag_ids: '',
      tags: '',
      submission_type: '',
      is_ttv_submission: false,
    };
  }, [defaultCompanyId]);

  const [selectedNpsResponse, setSelectedNpsResponse] =
    React.useState<NpsResponse>(getDefaultNpsResponse());

  const prevOpen = usePrevious(open);
  const justOpened = !prevOpen && open;
  React.useEffect(() => {
    if (justOpened) {
      setComment('');
      setSelectedNpsResponse(getDefaultNpsResponse());
    }
  }, [justOpened, getDefaultNpsResponse]);

  const user = getUserData();
  const canEdit = user?.permissions?.includes('nps') || false;
  const [viewMoreDialogOpen, setViewMoreDialogOpen] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState<Job[]>();
  const [dialogDataLoading, setDialogDataLoading] = React.useState(false);

  const handleViewMoreDialog = async (jobId: string) => {
    setDialogDataLoading(true);
    try {
      const jobDetails = await fetchJobDetails(jobId);
      setSelectedJob(jobDetails);
      setViewMoreDialogOpen(true);
    } catch {
      snackbar.error('Could not load vacancy details. Please try again.');
    } finally {
      setDialogDataLoading(false);
    }
  };

  const fetchComments = React.useCallback(() => {
    const npsId = loadedNpsIdRef.current;
    if (!npsId) {
      return;
    }
    setLoadingComments(true);
    fetchNpsComments(npsId)
      .then((d) => {
        setComments(d);
      })
      .catch(() => {
        snackbarRef.current.error('Could not load comments. Please try again.');
      })
      .finally(() => {
        setLoadingComments(false);
      });
  }, [setLoadingComments, setComments]);

  const fetchTagsData = React.useCallback(
    async (preselectedTagIds: string) => {
      setLoadingTags(true);
      try {
        const newTags = await fetchTags();
        setTags(newTags);

        if (!preselectedTagIds) {
          setSelectedTags([]);
          return;
        }

        const currentTagIds = preselectedTagIds.split(', ') ?? [];
        setSelectedTags(
          newTags
            .slice()
            .filter((t) => currentTagIds.includes(t.id.toString())),
        );
      } catch {
        snackbarRef.current.error('Could not load tags. Please try again.');
      } finally {
        setLoadingTags(false);
      }
    },
    [setLoadingTags, setTags],
  );

  const fetchCompanies = React.useCallback(() => {
    if (!selectedNpsResponse.brand_identifier || selectedNpsResponseId) {
      return;
    }

    setLoadingCompanies(true);
    fetchNpsCompanies(selectedNpsResponse.brand_identifier)
      .then((d) => {
        setCompanies(d);
      })
      .catch(() => {
        snackbarRef.current.error(
          'Could not load companies. Please try again.',
        );
      })
      .finally(() => {
        setLoadingCompanies(false);
      });
  }, [
    setLoadingCompanies,
    setCompanies,
    selectedNpsResponse.brand_identifier,
    selectedNpsResponseId,
  ]);

  const fetchLeaders = React.useCallback(() => {
    setLoadingNpsLeaders(true);
    fetchNpsLeaders()
      .then((d) => {
        setNpsLeaders(d);
      })
      .catch(() => {
        snackbarRef.current.error(
          'Could not load reviewees. Please try again.',
        );
      })
      .finally(() => {
        setLoadingNpsLeaders(false);
      });
  }, [setLoadingNpsLeaders, setNpsLeaders]);

  const fetchTalexioUsers = React.useCallback(() => {
    setLoadingTalexioUsers(true);
    fetchNpsTalexioUsers()
      .then((users) => {
        setTalexioUsers(users);
      })
      .catch(() => {
        snackbarRef.current.error(
          'Could not load Talexio users. Please try again.',
        );
      })
      .finally(() => {
        setLoadingTalexioUsers(false);
      });
  }, []);

  const loadDetails = React.useCallback(
    (id: string) => {
      loadedNpsIdRef.current = id;
      setLoadingNps(true);
      setLoadError(false);
      fetchNpsDetails(id)
        .then((d) => {
          fetchComments();
          fetchTagsData(d?.tag_ids ?? '');
          fetchLeaders();
          fetchCompanies();
          setSelectedNpsResponse(d);
          setdateHandled(d?.date_handled ? new Date(d.date_handled) : null);
          setTtvSubmission(d.is_ttv_submission || false);
        })
        .catch(() => {
          setLoadError(true);
          snackbarRef.current.error(
            'Could not load this NPS submission. Please try again.',
          );
        })
        .finally(() => {
          setLoadingNps(false);
        });
    },
    [
      setSelectedNpsResponse,
      fetchComments,
      fetchTagsData,
      fetchLeaders,
      fetchCompanies,
    ],
  );

  const fetchNpsData = React.useCallback(() => {
    if (!open || prevOpen === open) {
      return;
    }
    if (!selectedNpsResponseId) {
      setSelectedNpsResponse(getDefaultNpsResponse());
      fetchTagsData('');
      fetchLeaders();
      fetchCompanies();
      setdateHandled(null);
      setTtvSubmission(false);
      return;
    }
    loadDetails(selectedNpsResponseId);
  }, [
    setSelectedNpsResponse,
    selectedNpsResponseId,
    getDefaultNpsResponse,
    open,
    prevOpen,
    fetchTagsData,
    fetchLeaders,
    fetchCompanies,
    loadDetails,
  ]);

  React.useEffect(() => {
    fetchNpsData();
  }, [fetchNpsData, selectedNpsResponseId]);

  React.useEffect(() => {
    if (selectedNpsResponseId) {
      return;
    }
    let newUserType = '';
    switch (selectedNpsResponse.brand_identifier) {
      case 'tlx':
        newUserType = 'admin';
        break;
      case 'knt':
      case 'pm':
      case 'jim':
      case 'sim':
        newUserType = 'client';
        break;
    }
    setSelectedNpsResponse((n) => ({
      ...n,
      user_type: newUserType,
      company_id: undefined,
    }));
  }, [
    selectedNpsResponse.brand_identifier,
    setSelectedNpsResponse,
    selectedNpsResponseId,
  ]);

  React.useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies, selectedNpsResponse.brand_identifier]);

  React.useEffect(() => {
    if (
      !selectedNpsResponseId &&
      selectedNpsResponse.brand_identifier === 'tlx'
    ) {
      fetchTalexioUsers();
    }
  }, [
    selectedNpsResponseId,
    selectedNpsResponse.brand_identifier,
    fetchTalexioUsers,
  ]);

  React.useEffect(() => {
    setHistory([]);
    if (!open || !selectedNpsResponseId) {
      return;
    }
    fetchNpsHistory(selectedNpsResponseId)
      .then(setHistory)
      .catch(() => {
        snackbarRef.current.error(
          'Could not load rating history. Please try again.',
        );
      });
  }, [open, selectedNpsResponseId]);

  const loading = loadingComments || loadingTags || loadingNps;

  const filteredTags = React.useMemo(() => {
    if (tagParityFilter === 'all') {
      return tags;
    }
    return tags.filter((tag) => tag.parity === tagParityFilter);
  }, [tags, tagParityFilter]);

  const commentRefs = React.useRef<Record<number, NpsCommentBoxHandle>>({});

  const historyIndex = history.findIndex(
    (h) => h.platform_nps_id === selectedNpsResponse.platform_nps_id,
  );
  // Show at most 10 chips, keeping the open submission in view
  const historyStart = Math.max(
    0,
    Math.min(historyIndex - 5, history.length - 10),
  );
  const visibleHistory = history.slice(historyStart, historyStart + 10);
  const ratedHistory = history.filter((h) => h.rating !== -1);
  const historyAverage =
    ratedHistory.reduce((sum, h) => sum + h.rating, 0) /
    (ratedHistory.length || 1);

  const openHistoryItem = (npsId: string) => {
    // Comment boxes of the old submission must not be saved with this one
    commentRefs.current = {};
    setComment('');
    loadDetails(npsId);
  };

  // Grey until the submission (and so its brand) has loaded
  const brandColor =
    (!loadingNps &&
      brandList.find(
        (b) => b.brand_identifier === selectedNpsResponse.brand_identifier,
      )?.color) ||
    grey[400];

  // Most users cannot open Jira, so the chip colour carries the card status
  const jiraStatus = selectedNpsResponse.jira_status || 'Unknown';
  let jiraColor: string = amber[100];
  if (
    [
      'Closed',
      'Done',
      'Resolved',
      'Live OK',
      'Live - Test Successful',
      'UAT - Test Successful',
      'UAT OK',
      'On Live',
    ].includes(jiraStatus)
  ) {
    jiraColor = green[100];
  } else if (['Cancelled', 'Non Issue'].includes(jiraStatus)) {
    jiraColor = grey[200];
  }

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      PaperProps={{ style: { borderTop: `4px solid ${brandColor}` } }}
      aria-labelledby="nps-dialog-title"
      disableBackdropClick
      disableEscapeKeyDown
      onClose={() => onClose && onClose()}
    >
      <DialogContent className={classes.dialogContent}>
        {loadingNps ? (
          <Skeleton variant="rect" style={{ height: 400 }} />
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              selectedNpsResponseId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => loadDetails(selectedNpsResponseId)}
                >
                  Retry
                </Button>
              ) : undefined
            }
          >
            Could not load this NPS submission. Check your connection and try
            again.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              {/* HEADER SECTION */}
              <div className={classes.headerSection}>
                <Typography variant="h5" id="nps-dialog-title">
                  {selectedNpsResponseId ? 'Edit' : 'Create'} NPS Submission
                </Typography>
                {selectedNpsResponseId && (
                  <Chip
                    size="small"
                    color="primary"
                    label={
                      selectedNpsResponse?.submission_type?.startsWith('Manual')
                        ? 'Manual Submission'
                        : 'User Submission'
                    }
                  />
                )}
                {selectedNpsResponseId &&
                  !!selectedNpsResponse?.testimonial && (
                    <Chip
                      size="small"
                      color="primary"
                      className={classes.testimonialChip}
                      icon={<FormatQuote />}
                      label="Testimonial"
                      title="This submission can be used as a testimonial"
                    />
                  )}
                {selectedNpsResponseId &&
                  selectedNpsResponse.jira_issue_key && (
                    <Chip
                      size="small"
                      clickable
                      component="a"
                      href={`${Config.JIRA_BASE_URL}browse/${selectedNpsResponse.jira_issue_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      avatar={<Avatar src="/jira.ico" />}
                      label={`${selectedNpsResponse.jira_issue_key} · ${jiraStatus}`}
                      title="Open the Jira card"
                      style={{ backgroundColor: jiraColor }}
                    />
                  )}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={!canEdit}
                      checked={!!isTtvSubmission}
                      onChange={(e) => setTtvSubmission(e.target.checked)}
                    />
                  }
                  label="Team Voice"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={!canEdit}
                      checked={!!dateHandled}
                      onChange={(e, checked) =>
                        setdateHandled(checked ? new Date() : null)
                      }
                    />
                  }
                  label="Handled"
                />
              </div>

              {/* COMBINED SUBMISSION, USER & ROLE DETAILS SECTION */}
              <div className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                >
                  Submission, User & Role Details
                </Typography>
                <Grid container spacing={1}>
                  {/* Brand */}
                  <Grid item xs={12} sm={6}>
                    <FormControl className={classes.formControl} fullWidth>
                      {selectedNpsResponseId ? (
                        <React.Fragment>
                          <FormLabel className={classes.label}>Brand</FormLabel>
                          <div className={classes.capitlizeText}>
                            {selectedNpsResponse.brand_identifier
                              ? resolvePlatformNameFromPrefix(
                                  selectedNpsResponse.brand_identifier,
                                )
                              : 'N/A'}
                          </div>
                        </React.Fragment>
                      ) : (
                        <Autocomplete
                          options={brandList}
                          getOptionLabel={(o) => o.label}
                          renderInput={(params) => (
                            <TextField
                              variant="outlined"
                              label="Brand"
                              {...params}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              className={classes.compactTextfield}
                            />
                          )}
                          disableClearable={true}
                          value={brandList.find(
                            (b) =>
                              b.brand_identifier ===
                              selectedNpsResponse.brand_identifier,
                          )}
                          onChange={(e, newValue) => {
                            setSelectedNpsResponse((n) => ({
                              ...n,
                              brand_identifier: newValue.brand_identifier,
                            }));
                          }}
                        />
                      )}
                    </FormControl>
                  </Grid>

                  {/* User Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl className={classes.formControl} fullWidth>
                      {selectedNpsResponseId ? (
                        <React.Fragment>
                          <FormLabel className={classes.label}>
                            User type
                          </FormLabel>
                          <div className={classes.capitlizeText}>
                            {Boolean(selectedNpsResponse.user_type)
                              ? selectedNpsResponse.user_type
                              : 'N/A'}
                          </div>
                        </React.Fragment>
                      ) : (
                        <Autocomplete
                          options={userTypes.filter(
                            (ut) =>
                              ut.brand_identifier ===
                              selectedNpsResponse.brand_identifier,
                          )}
                          getOptionLabel={(o) => o.label}
                          renderInput={(params) => (
                            <TextField
                              variant="outlined"
                              label="User type"
                              {...params}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              className={classes.compactTextfield}
                            />
                          )}
                          disableClearable={true}
                          value={userTypes.find(
                            (b) =>
                              b.key === selectedNpsResponse.user_type &&
                              b.brand_identifier ===
                                selectedNpsResponse.brand_identifier,
                          )}
                          onChange={(e, newValue) => {
                            setSelectedNpsResponse((n) => ({
                              ...n,
                              user_type: newValue.key,
                            }));
                          }}
                        />
                      )}
                    </FormControl>
                  </Grid>

                  {/* Talexio User Autocomplete (Create Mode) */}
                  {!selectedNpsResponseId &&
                    selectedNpsResponse.brand_identifier === 'tlx' &&
                    selectedNpsResponse.user_type === 'admin' && (
                      <Grid item xs={12}>
                        <FormControl className={classes.formControl} fullWidth>
                          <Autocomplete
                            loading={loadingTalexioUsers}
                            options={talexioUsers}
                            getOptionLabel={(user) =>
                              `${user.first_name} ${user.last_name} (${user.company})`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Talexio User"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                className={classes.compactTextfield}
                              />
                            )}
                            onChange={(_, user) => {
                              if (user) {
                                setSelectedNpsResponse((prev) => ({
                                  ...prev,
                                  user_first_name: user.first_name,
                                  user_last_name: user.last_name,
                                  user_email: user.email_address,
                                  user_contact_number: user.mobile_number,
                                  company: user.company,
                                  company_id: user.company_id,
                                }));
                              }
                            }}
                          />
                        </FormControl>
                      </Grid>
                    )}

                  {/* User First Name & Last Name */}
                  {selectedNpsResponseId ? (
                    <React.Fragment>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          className={`${classes.formControl} ${classes.disabledInput}`}
                          fullWidth
                        >
                          <TextField
                            value={selectedNpsResponse.user_first_name}
                            variant="outlined"
                            label="First Name"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={!canEdit}
                            className={classes.capitlizeText}
                            onChange={(e) => {
                              const newFirstName = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_first_name: newFirstName,
                              }));
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          className={`${classes.formControl} ${classes.disabledInput}`}
                          fullWidth
                        >
                          <TextField
                            value={selectedNpsResponse.user_last_name}
                            variant="outlined"
                            label="Last Name"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={!canEdit}
                            className={classes.capitlizeText}
                            onChange={(e) => {
                              const newLastName = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_last_name: newLastName,
                              }));
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Grid item xs={12} sm={6}>
                        <FormControl className={classes.formControl} fullWidth>
                          <TextField
                            fullWidth
                            value={selectedNpsResponse.user_first_name}
                            variant="outlined"
                            placeholder="First name"
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_first_name: newValue,
                              }));
                            }}
                            required
                            label={`User first name`}
                            InputLabelProps={{ shrink: true }}
                            className={classes.compactTextfield}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl className={classes.formControl} fullWidth>
                          <TextField
                            fullWidth
                            value={selectedNpsResponse.user_last_name}
                            variant="outlined"
                            placeholder="Last name"
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_last_name: newValue,
                              }));
                            }}
                            required
                            label={`User last name`}
                            InputLabelProps={{ shrink: true }}
                            className={classes.compactTextfield}
                          />
                        </FormControl>
                      </Grid>
                    </React.Fragment>
                  )}

                  {/* User Email & Contact Number */}
                  {selectedNpsResponseId ? (
                    <React.Fragment>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          className={`${classes.formControl} ${classes.disabledInput}`}
                          fullWidth
                        >
                          <TextField
                            value={selectedNpsResponse.user_email}
                            variant="outlined"
                            label="Email"
                            fullWidth
                            type="email"
                            InputLabelProps={{ shrink: true }}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const newEmail = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_email: newEmail,
                              }));
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          className={`${classes.formControl} ${classes.disabledInput}`}
                          fullWidth
                        >
                          <TextField
                            value={selectedNpsResponse.user_contact_number}
                            variant="outlined"
                            label="Contact number"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={!canEdit}
                            className={classes.capitlizeText}
                            onChange={(e) => {
                              const newContactNumber = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_contact_number: newContactNumber,
                              }));
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Grid
                        item
                        xs={12}
                        sm={
                          selectedNpsResponse.user_type &&
                          ![
                            'candidate',
                            'candidate-tcn',
                            'candidate-executive',
                          ].includes(selectedNpsResponse.user_type)
                            ? 3
                            : 6
                        }
                      >
                        <FormControl className={classes.formControl} fullWidth>
                          <TextField
                            fullWidth
                            value={selectedNpsResponse.user_email}
                            variant="outlined"
                            placeholder="Email address"
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_email: newValue,
                              }));
                            }}
                            required
                            label={`User email`}
                            InputLabelProps={{ shrink: true }}
                            className={classes.compactTextfield}
                          />
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={
                          selectedNpsResponse.user_type &&
                          ![
                            'candidate',
                            'candidate-tcn',
                            'candidate-executive',
                          ].includes(selectedNpsResponse.user_type)
                            ? 3
                            : 6
                        }
                      >
                        <FormControl className={classes.formControl} fullWidth>
                          <TextField
                            fullWidth
                            value={selectedNpsResponse.user_contact_number}
                            variant="outlined"
                            placeholder="Contact number"
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                user_contact_number: newValue,
                              }));
                            }}
                            label={`Contact number`}
                            InputLabelProps={{ shrink: true }}
                            className={classes.compactTextfield}
                          />
                        </FormControl>
                      </Grid>
                    </React.Fragment>
                  )}

                  {/* Company (Conditional) */}
                  {![
                    'candidate',
                    'candidate-tcn',
                    'candidate-executive',
                  ].includes(selectedNpsResponse.user_type ?? '') && (
                    <Grid
                      item
                      xs={12}
                      sm={
                        selectedNpsResponseId
                          ? 6
                          : selectedNpsResponse.user_type &&
                            ![
                              'candidate',
                              'candidate-tcn',
                              'candidate-executive',
                            ].includes(selectedNpsResponse.user_type)
                          ? 6
                          : 12
                      }
                    >
                      <FormControl className={classes.formControl} fullWidth>
                        {selectedNpsResponseId ? (
                          <React.Fragment>
                            <FormLabel className={classes.label}>
                              Company
                            </FormLabel>
                            <div className={classes.capitlizeText}>
                              {Boolean(selectedNpsResponse.company)
                                ? selectedNpsResponse.company
                                : 'N/A'}
                            </div>
                          </React.Fragment>
                        ) : (
                          <Autocomplete
                            loading={loadingCompanies}
                            disabled={loadingCompanies}
                            getOptionLabel={(o) => o.company_title}
                            options={companies}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Company"
                                placeholder={
                                  loadingCompanies ? 'Loading...' : 'Company'
                                }
                                {...params}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                className={classes.compactTextfield}
                              />
                            )}
                            value={
                              companies.find(
                                (f) => f.id === selectedNpsResponse.company_id,
                              ) ?? null
                            }
                            onChange={(e, newValue) => {
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                company_id: newValue?.id,
                                company: newValue?.company_title,
                              }));
                            }}
                          />
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {/* Designation (Edit Mode Only) */}
                  {selectedNpsResponseId && (
                    <Grid item xs={12} sm={6}>
                      <FormControl className={classes.formControl} fullWidth>
                        <FormLabel className={classes.label}>
                          Designation
                        </FormLabel>
                        <Typography
                          variant="body1"
                          className={classes.capitlizeText}
                        >
                          {Boolean(selectedNpsResponse.user_designation)
                            ? selectedNpsResponse.user_designation
                            : 'N/A'}
                        </Typography>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Reviewee (Conditional on brand) */}
                  {['knt', 'pm'].includes(
                    selectedNpsResponse.brand_identifier ?? '',
                  ) && (
                    <Grid item xs={12} sm={selectedNpsResponseId ? 6 : 12}>
                      <FormControl className={classes.formControl} fullWidth>
                        {selectedNpsResponseId ? (
                          <React.Fragment>
                            <FormLabel className={classes.label}>
                              Reviewee
                            </FormLabel>
                            <div>
                              {Boolean(selectedNpsResponse.reviewee)
                                ? selectedNpsResponse.reviewee
                                : 'N/A'}{' '}
                              {selectedNpsResponse.team_identifier
                                ? `(${selectedNpsResponse.team_identifier})`
                                : null}
                            </div>
                          </React.Fragment>
                        ) : (
                          <Autocomplete
                            loading={loadingNpsLeaders}
                            getOptionLabel={(o) =>
                              `${o.reviewee}${
                                o.reviewee !== 'All'
                                  ? ` (${o.team_identifier})`
                                  : ''
                              }`
                            }
                            options={npsLeaders}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Reviewee"
                                {...params}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                className={classes.compactTextfield}
                              />
                            )}
                            value={
                              npsLeaders.find(
                                (f) =>
                                  f.reviewee === selectedNpsResponse.reviewee &&
                                  f.team_identifier ===
                                    selectedNpsResponse.team_identifier,
                              ) ?? null
                            }
                            onChange={(e, newValue) => {
                              setSelectedNpsResponse((n) => ({
                                ...n,
                                reviewee: newValue?.reviewee ?? undefined,
                                team_identifier:
                                  newValue?.team_identifier ?? undefined,
                              }));
                            }}
                          />
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {/* Dates (Edit Mode Only) */}
                  {selectedNpsResponseId && (
                    <React.Fragment>
                      <Grid item xs={12} sm={6}>
                        <FormControl className={classes.formControl} fullWidth>
                          <FormLabel className={classes.label}>
                            Date submitted
                          </FormLabel>
                          <div>
                            {selectedNpsResponse.date_submitted
                              ? format(
                                  parseISO(selectedNpsResponse.date_submitted),
                                  DateTimeFormat.LONG_FRIENDLY,
                                )
                              : 'Unsubmitted'}
                          </div>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl className={classes.formControl} fullWidth>
                          <FormLabel className={classes.label}>
                            Date sent
                          </FormLabel>
                          <div>
                            {selectedNpsResponse.date_sent &&
                            selectedNpsResponse.date_sent !== 'NaT'
                              ? format(
                                  parseISO(selectedNpsResponse.date_sent),
                                  DateTimeFormat.LONG_FRIENDLY,
                                )
                              : 'N/A'}
                          </div>
                        </FormControl>
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              </div>

              {/* TAGS SECTION */}
              <div className={classes.section}>
                <div className={classes.sectionTitleContainer}>
                  <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                  >
                    Tags
                  </Typography>
                  <FormControl component="fieldset" style={{ width: 'auto' }}>
                    <ToggleButtonGroup
                      className={classes.filterGroup}
                      value={tagParityFilter}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setTagParityFilter(newValue as TagParity);
                        }
                      }}
                      aria-label="tag parity filter"
                      size="small"
                    >
                      <ToggleButton
                        value="all"
                        aria-label="all tags"
                        className={classes.toggleButton}
                      >
                        All
                      </ToggleButton>
                      <ToggleButton
                        value="positive"
                        aria-label="positive tags"
                        className={classes.toggleButton}
                      >
                        Positive
                      </ToggleButton>
                      <ToggleButton
                        value="negative"
                        aria-label="negative tags"
                        className={classes.toggleButton}
                      >
                        Negative
                      </ToggleButton>
                      <ToggleButton
                        value="other"
                        aria-label="other tags"
                        className={classes.toggleButton}
                      >
                        Other
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </div>
                <FormControl fullWidth className={classes.denseInput}>
                  <Autocomplete
                    options={filteredTags}
                    getOptionSelected={(t1, t2) =>
                      t1.id.toString() === t2.id.toString()
                    }
                    getOptionLabel={(t) => t.tag_name}
                    loading={loadingTags}
                    disabled={loading || !canEdit}
                    renderInput={(params) => (
                      <TextField
                        variant="outlined"
                        label="Tags"
                        {...params}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    multiple={true}
                    value={selectedTags}
                    onChange={(e, newTags) => {
                      setSelectedTags(newTags);
                    }}
                  />
                </FormControl>
              </div>
            </Grid>

            <Grid item xs={12} md={5}>
              <div className={classes.rightColumn}>
                <div className={classes.section}>
                  <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                  >
                    Rating
                  </Typography>
                  <FormControl fullWidth>
                    <ToggleButtonGroup
                      value={
                        selectedNpsResponse.rating !== -1
                          ? selectedNpsResponse.rating?.toString()
                          : null
                      }
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          const newRating = parseInt(newValue, 10);
                          setSelectedNpsResponse((n) => ({
                            ...n,
                            rating: newRating,
                          }));
                        }
                      }}
                      className={classes.ratingGroup}
                      aria-label="nps rating 0-10"
                    >
                      {[...Array(11).keys()].map((s) => (
                        <ToggleButton
                          key={s}
                          value={s.toString()}
                          disabled={loading || !canEdit}
                          className={classes.ratingButton}
                          aria-label={`rating ${s}`}
                        >
                          {s.toString()}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <ToggleButton
                      value="-1"
                      selected={selectedNpsResponse.rating === -1}
                      disabled={loading || !canEdit}
                      className={classes.noSubmissionButton}
                      aria-label="no submission"
                      onClick={() => {
                        setSelectedNpsResponse((n) => ({
                          ...n,
                          rating: -1,
                        }));
                      }}
                    >
                      No submission
                    </ToggleButton>
                  </FormControl>
                </div>

                {history.length > 1 && (
                  <div className={classes.section}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionTitle}
                    >
                      Rating history{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        color="textSecondary"
                      >
                        {ratedHistory.length} submissions · avg{' '}
                        {historyAverage.toFixed(1)}
                      </Typography>
                    </Typography>
                    <div className={classes.historyChips}>
                      {visibleHistory.map((h) => (
                        <Tooltip
                          key={h.platform_nps_id}
                          title={
                            h.submitted_on
                              ? format(parseISO(h.submitted_on), 'd MMM yyyy')
                              : ''
                          }
                        >
                          <ButtonBase
                            className={[
                              classes.historyChip,
                              h.rating_type === 'promoter'
                                ? classes.historyChipPromoter
                                : '',
                              h.rating_type === 'detractor'
                                ? classes.historyChipDetractor
                                : '',
                              h.platform_nps_id ===
                              selectedNpsResponse.platform_nps_id
                                ? classes.historyChipCurrent
                                : '',
                            ].join(' ')}
                            disabled={loading}
                            aria-label={`Open submission ${h.platform_nps_id}`}
                            onClick={() => openHistoryItem(h.platform_nps_id)}
                          >
                            {h.rating === -1 ? '–' : h.rating}
                          </ButtonBase>
                        </Tooltip>
                      ))}
                    </div>
                    <div className={classes.historyFooter}>
                      <Typography variant="caption" color="textSecondary">
                        {visibleHistory[0].submitted_on &&
                          format(
                            parseISO(visibleHistory[0].submitted_on),
                            'd MMM yyyy',
                          )}
                      </Typography>
                      <div>
                        <Button
                          size="small"
                          startIcon={<ChevronLeft />}
                          disabled={loading || historyIndex <= 0}
                          onClick={() =>
                            openHistoryItem(
                              history[historyIndex - 1].platform_nps_id,
                            )
                          }
                        >
                          Older
                        </Button>
                        <Typography variant="caption" color="textSecondary">
                          {historyIndex + 1} of {history.length}
                        </Typography>
                        <Button
                          size="small"
                          endIcon={<ChevronRight />}
                          disabled={
                            loading || historyIndex >= history.length - 1
                          }
                          onClick={() =>
                            openHistoryItem(
                              history[historyIndex + 1].platform_nps_id,
                            )
                          }
                        >
                          Newer
                        </Button>
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        {visibleHistory[visibleHistory.length - 1]
                          .submitted_on &&
                          format(
                            parseISO(
                              visibleHistory[visibleHistory.length - 1]
                                .submitted_on!,
                            ),
                            'd MMM yyyy',
                          )}
                      </Typography>
                    </div>
                  </div>
                )}

                {selectedNpsResponse.platform_nps_id && (
                  <div className={classes.commentSection}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionTitle}
                    >
                      Comments
                    </Typography>
                    {loadingComments ? (
                      <Skeleton variant="rect" style={{ height: 100 }} />
                    ) : (
                      comments.map((c) => (
                        <NpsCommentBox
                          ref={(el) => {
                            if (el) commentRefs.current[c.id] = el;
                          }}
                          comment={c}
                          selectedNpsResponse={selectedNpsResponse}
                          key={`comment_${c.id}_${selectedNpsResponse.platform_nps_id}`}
                          canEdit={canEdit}
                          fetchComments={fetchComments}
                          reviewees={npsLeaders}
                          loadingReviewees={loadingNpsLeaders}
                        />
                      ))
                    )}
                    <form
                      onSubmit={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!selectedNpsResponse.platform_nps_id || !comment) {
                          return false;
                        }

                        setLoadingComments(true);
                        try {
                          await addComment(
                            selectedNpsResponse.platform_nps_id,
                            comment,
                          );
                          setComment('');
                          fetchComments();
                        } catch {
                          snackbar.error(
                            'Failed to add comment. Please try again.',
                          );
                          setLoadingComments(false);
                        }
                        return false;
                      }}
                    >
                      {user?.permissions?.includes('nps') && (
                        <RichTextEditor
                          placeholder="Type here to add your comment..."
                          onChange={setComment}
                          value={comment}
                          autoFocus
                          helperText="Comments are saved automatically when added. You can add more detail such as the action date by clicking the downward arrow."
                          endAdornment={
                            <IconButton
                              type="submit"
                              size="small"
                              aria-label="Add comment"
                              disabled={loading || !comment}
                            >
                              <Send />
                            </IconButton>
                          }
                        />
                      )}
                    </form>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        {selectedNpsResponse.platform_nps_id && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginRight: 'auto' }}
            title="NPS Submission ID"
          >
            ID: {selectedNpsResponse.platform_nps_id}
          </Typography>
        )}
        {canEdit && (
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              if (!selectedNpsResponseId) {
                const trimmedEmail = (
                  selectedNpsResponse.user_email ?? ''
                ).trim();
                const missing: string[] = [];
                if (!(selectedNpsResponse.user_first_name ?? '').trim()) {
                  missing.push('first name');
                }
                if (!(selectedNpsResponse.user_last_name ?? '').trim()) {
                  missing.push('last name');
                }
                if (!trimmedEmail) {
                  missing.push('email');
                }
                if (missing.length > 0) {
                  snackbar.error(
                    `Please fill in the required fields: ${missing.join(
                      ', ',
                    )}.`,
                  );
                  return;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
                  snackbar.error('Please enter a valid email address.');
                  return;
                }
              }
              setLoadingNps(true);
              try {
                await Promise.all(
                  Object.values(commentRefs.current).map((r) => r.save()),
                );

                if (selectedNpsResponse.platform_nps_id) {
                  const currentTagIds =
                    selectedNpsResponse.tag_ids?.split(', ') ?? [];
                  const addTags = selectedTags
                    .filter((t) => !currentTagIds.includes(t.id.toString()))
                    .map((t) => t.id.toString());
                  const removeTags = currentTagIds.filter(
                    (id) => !selectedTags.some((t) => t.id.toString() === id),
                  );
                  await updateNps(
                    selectedNpsResponse.platform_nps_id,
                    selectedNpsResponse.rating ?? -1,
                    selectedNpsResponse.user_email ?? '',
                    selectedNpsResponse.user_first_name ?? '',
                    selectedNpsResponse.user_last_name ?? '',
                    selectedNpsResponse.user_contact_number ?? '',
                    selectedNpsResponse.company ?? '',
                    selectedNpsResponse.company_id ?? '',
                    selectedNpsResponse.user_type ?? '',
                    selectedNpsResponse.brand_identifier ?? '',
                    selectedNpsResponse.reviewee ?? '',
                    dateHandled ? dateHandled.toISOString() : '',
                    isTtvSubmission,
                    addTags,
                    removeTags,
                  );
                } else {
                  await addNps(
                    selectedNpsResponse.rating ?? -1,
                    selectedNpsResponse.user_email ?? '',
                    selectedNpsResponse.user_first_name ?? '',
                    selectedNpsResponse.user_last_name ?? '',
                    selectedNpsResponse.user_contact_number ?? '',
                    selectedNpsResponse.company ?? '',
                    selectedNpsResponse.company_id ?? '',
                    selectedNpsResponse.user_type ?? '',
                    selectedNpsResponse.brand_identifier ?? '',
                    selectedNpsResponse.reviewee ?? '',
                    selectedNpsResponse.is_ttv_submission || false,
                    selectedTags.map((t) => t.id),
                  );
                }
              } catch {
                snackbar.error('Failed to save changes. Please try again.');
              }
              setLoadingNps(false);
              onClose && onClose();
            }}
            disabled={loading}
          >
            Save changes
          </Button>
        )}

        {selectedNpsResponse.user_type === 'client' &&
          selectedNpsResponse.brand_identifier === 'knt' &&
          selectedNpsResponse.product_id && (
            <Button
              color="secondary"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                if (selectedNpsResponse.product_id) {
                  handleViewMoreDialog(selectedNpsResponse.product_id);
                }
              }}
              disabled={dialogDataLoading}
            >
              Vacancy Details
            </Button>
          )}

        {selectedNpsResponse.user_type === 'candidate' &&
          selectedNpsResponse.brand_identifier === 'knt' &&
          selectedNpsResponse.user_id && (
            <Button
              color="secondary"
              startIcon={<OpenInNewIcon />}
              href={`https://konnekt.talexiohr.com/ats/candidates/${selectedNpsResponse.user_id}`}
              target="_blank"
              rel="noopener"
            >
              View Candidate
            </Button>
          )}

        <Button onClick={() => onClose()} color="default" disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
      <JobDetailsDialog
        open={viewMoreDialogOpen}
        job={selectedJob!}
        loading={dialogDataLoading}
        onClose={() => setViewMoreDialogOpen(false)}
      />
    </Dialog>
  );
};
